import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  ReportBarChart,
  ReportLineChart,
} from '@/components/reports/shared';
import {
  TrendingUp,
  Calendar,
  BarChart3,
  LineChart as LineChartIcon,
} from 'lucide-react';

interface TrendChartProps {
  data: Array<{
    month: string;
    budgets: number;
    value: number;
    conversion: number;
    approved: number;
    rejected: number;
    pending: number;
  }>;
  loading?: boolean;
}

export function TrendChart({ data, loading = false }: TrendChartProps) {
  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-80 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Prepare data for bar chart - budgets by month
  const budgetVolumeData = data.map(item => ({
    month: item.month,
    'Orçamentos': item.budgets,
    'Aprovados': item.approved,
    'Rejeitados': item.rejected,
    'Pendentes': item.pending,
  }));

  // Prepare data for line chart - conversion trends
  const conversionTrendData = data.map(item => ({
    month: item.month,
    'Taxa de Conversão': item.conversion,
    'Média Histórica': data.reduce((sum, d) => sum + d.conversion, 0) / data.length,
  }));

  // Prepare data for value trends
  const valueTrendData = data.map(item => ({
    month: item.month,
    'Valor Total': item.value,
    'Ticket Médio': item.value / (item.budgets || 1),
  }));

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  // Calculate trends
  const latestMonth = data[data.length - 1];
  const previousMonth = data[data.length - 2];

  const calculateTrend = (current: number, previous: number) => {
    if (previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  };

  const budgetTrend = calculateTrend(latestMonth?.budgets || 0, previousMonth?.budgets || 0);
  const conversionTrend = calculateTrend(latestMonth?.conversion || 0, previousMonth?.conversion || 0);
  const valueTrend = calculateTrend(latestMonth?.value || 0, previousMonth?.value || 0);

  return (
    <div className="space-y-6">
      {/* Trend Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-elevated">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Orçamentos no Mês</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{latestMonth?.budgets || 0}</div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
              <TrendingUp className={`h-3 w-3 ${budgetTrend >= 0 ? 'text-green-500' : 'text-red-500'}`} />
              <span className={budgetTrend >= 0 ? 'text-green-500' : 'text-red-500'}>
                {budgetTrend >= 0 ? '+' : ''}{budgetTrend.toFixed(1)}%
              </span>
              <span>vs mês anterior</span>
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taxa de Conversão</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(latestMonth?.conversion || 0).toFixed(1)}%</div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
              <TrendingUp className={`h-3 w-3 ${conversionTrend >= 0 ? 'text-green-500' : 'text-red-500'}`} />
              <span className={conversionTrend >= 0 ? 'text-green-500' : 'text-red-500'}>
                {conversionTrend >= 0 ? '+' : ''}{conversionTrend.toFixed(1)}%
              </span>
              <span>vs mês anterior</span>
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Valor Mensal</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(latestMonth?.value || 0)}</div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
              <TrendingUp className={`h-3 w-3 ${valueTrend >= 0 ? 'text-green-500' : 'text-red-500'}`} />
              <span className={valueTrend >= 0 ? 'text-green-500' : 'text-red-500'}>
                {valueTrend >= 0 ? '+' : ''}{valueTrend.toFixed(1)}%
              </span>
              <span>vs mês anterior</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Budget Volume Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Volume de Orçamentos por Mês
          </CardTitle>
          <CardDescription>
            Quantidade de orçamentos por status ao longo do tempo
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ReportBarChart
            data={budgetVolumeData}
            dataKeys={[
              { key: 'Orçamentos', label: 'Total', color: 'hsl(var(--chart-1))' },
              { key: 'Aprovados', label: 'Aprovados', color: 'hsl(var(--chart-2))' },
              { key: 'Pendentes', label: 'Pendentes', color: 'hsl(var(--chart-3))' },
              { key: 'Rejeitados', label: 'Rejeitados', color: 'hsl(var(--chart-4))' },
            ]}
            xAxisKey="month"
            height={350}
            stacked={false}
            showGrid={true}
            formatValue={(value) => value.toString()}
          />
        </CardContent>
      </Card>

      {/* Conversion Rate Trend */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LineChartIcon className="h-5 w-5" />
            Tendência de Conversão
          </CardTitle>
          <CardDescription>
            Evolução da taxa de conversão ao longo dos meses
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ReportLineChart
            data={conversionTrendData}
            dataKeys={[
              {
                key: 'Taxa de Conversão',
                label: 'Taxa de Conversão',
                color: 'hsl(var(--chart-1))',
                showArea: true
              },
              {
                key: 'Média Histórica',
                label: 'Média Histórica',
                color: 'hsl(var(--chart-5))',
                showArea: false,
                showDots: false
              },
            ]}
            xAxisKey="month"
            height={300}
            showGrid={true}
            formatValue={formatPercentage}
          />
        </CardContent>
      </Card>

      {/* Value Trends */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Tendências de Valor
          </CardTitle>
          <CardDescription>
            Evolução do valor total e ticket médio
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ReportLineChart
            data={valueTrendData}
            dataKeys={[
              {
                key: 'Valor Total',
                label: 'Valor Total',
                color: 'hsl(var(--chart-2))',
                showArea: false
              },
              {
                key: 'Ticket Médio',
                label: 'Ticket Médio',
                color: 'hsl(var(--chart-3))',
                showArea: false
              },
            ]}
            xAxisKey="month"
            height={300}
            showGrid={true}
            formatValue={formatCurrency}
          />
        </CardContent>
      </Card>

      {/* Monthly Performance Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Resumo Mensal
          </CardTitle>
          <CardDescription>
            Detalhamento do desempenho mês a mês
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {data.slice(-6).reverse().map((month, index) => {
              const isLatest = index === 0;
              const performanceScore = month.conversion >= 70 ? 'excellent' : month.conversion >= 50 ? 'good' : 'poor';

              return (
                <div
                  key={month.month}
                  className={`p-4 border rounded-lg ${
                    isLatest ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="font-medium">{month.month}</div>
                      {isLatest && (
                        <Badge variant="default" className="text-xs">
                          Atual
                        </Badge>
                      )}
                      <Badge
                        variant={performanceScore === 'excellent' ? 'default' : 'secondary'}
                        className={`text-xs ${
                          performanceScore === 'excellent'
                            ? 'bg-green-100 text-green-800 border-green-200'
                            : performanceScore === 'good'
                            ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                            : 'bg-red-100 text-red-800 border-red-200'
                        }`}
                      >
                        {performanceScore === 'excellent' ? 'Excelente' : performanceScore === 'good' ? 'Bom' : 'Precisa Melhorar'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <div className="text-right">
                        <div className="font-medium">{month.budgets} orçamentos</div>
                        <div className="text-muted-foreground">{formatCurrency(month.value)}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">{month.conversion.toFixed(1)}% conversão</div>
                        <div className="text-muted-foreground">
                          {month.approved} aprovados
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}